import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useOrders } from '../hooks/useOrders.js';
import { useStages } from '../hooks/useStages.js';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { useWorkspaceStore } from '../stores/workspace.js';
import OrderModal from '../components/orders/OrderModal.js';
import type { Order, Stage } from '@mayksel/types';

const PRIORITY_DOT: Record<string, string> = {
  low: 'var(--color-text-muted)',
  normal: 'var(--color-text-secondary)',
  high: 'var(--color-amber)',
  urgent: 'var(--color-danger)',
};

interface Column {
  stageId: string | null;
  name: string;
  color: string;
  orders: Order[];
}

/** Moves an order to a new stage — calls PATCH /v1/orders/:id directly */
function useMoveOrder() {
  const { getToken } = useAuth();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ orderId, stageId }: { orderId: string; stageId: string | null }) => {
      const token = await getToken();
      return api.patch(`/v1/orders/${orderId}`, { stageId: stageId ?? undefined }, {
        token: token ?? undefined,
        workspaceId: workspaceId ?? undefined,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders', workspaceId] });
    },
  });
}

export default function Kanban() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const workspace = useWorkspaceStore((s) => s.workspace);

  const [createOpen, setCreateOpen] = useState(false);
  const [dragOrderId, setDragOrderId] = useState<string | null>(null);
  const [dragOverColId, setDragOverColId] = useState<string | null>(null);

  const { data: stagesData } = useStages();
  const { data: ordersData } = useOrders({ limit: 200, sortBy: 'order_number', sortDir: 'asc' });
  const moveOrder = useMoveOrder();

  const stages = stagesData ?? [];
  const allOrders = ordersData?.data ?? [];

  const orderLabel = workspace?.orderLabel ?? 'Order';
  const currency = workspace?.currency ?? 'USD';

  const columns: Column[] = [
    {
      stageId: null,
      name: t('kanban.no_stage'),
      color: 'var(--color-gray-300)',
      orders: allOrders.filter((o) => o.stageId === null),
    },
    ...stages.map((s: Stage) => ({
      stageId: s.id,
      name: s.name,
      color: s.color,
      orders: allOrders.filter((o) => o.stageId === s.id),
    })),
  ];

  function onCardDragStart(orderId: string) {
    setDragOrderId(orderId);
  }

  function onColDragOver(e: React.DragEvent, stageId: string | null) {
    e.preventDefault();
    setDragOverColId(stageId ?? '__none__');
  }

  function onColDrop(e: React.DragEvent, targetStageId: string | null) {
    e.preventDefault();
    setDragOverColId(null);

    const orderId = dragOrderId;
    setDragOrderId(null);
    if (!orderId) return;

    const order = allOrders.find((o) => o.id === orderId);
    if (!order || order.stageId === targetStageId) return;

    moveOrder.mutate(
      { orderId, stageId: targetStageId },
      { onError: () => toast.error(t('kanban.move_error')) },
    );
  }

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-5)', flexShrink: 0 }}>
        <h1 className="page-title">{t('kanban.title')}</h1>
        <button
          onClick={() => setCreateOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-4)', background: 'var(--color-brand)',
            color: 'var(--color-text-inverse)', border: 'none',
            borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-medium)', cursor: 'pointer',
          }}
        >
          <Plus size={16} />
          {`New ${orderLabel}`}
        </button>
      </div>

      {/* Board */}
      <div style={{
        display: 'flex', gap: 'var(--space-4)', overflowX: 'auto',
        paddingBottom: 'var(--space-4)', alignItems: 'flex-start',
      }}>
        {columns.map((col) => {
          const colKey = col.stageId ?? '__none__';
          const isDragOver = dragOverColId === colKey;

          return (
            <div
              key={colKey}
              onDragOver={(e) => onColDragOver(e, col.stageId)}
              onDrop={(e) => onColDrop(e, col.stageId)}
              style={{
                width: 272, flexShrink: 0,
                background: isDragOver ? 'var(--color-brand-pale)' : 'var(--color-bg-subtle)',
                border: `1px solid ${isDragOver ? 'var(--color-brand)' : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-lg)',
                transition: 'background 100ms, border-color 100ms',
                display: 'flex', flexDirection: 'column',
              }}
            >
              {/* Column header */}
              <div style={{
                padding: 'var(--space-3) var(--space-4)',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
                    ...(col.color.startsWith('var(')
                      ? { backgroundColor: col.color }
                      : { background: col.color }),
                  }} />
                  <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                    {col.name}
                  </span>
                </div>
                <span style={{
                  fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
                  background: 'var(--color-bg-muted)', borderRadius: 'var(--radius-full)',
                  padding: '1px 7px',
                }}>
                  {col.orders.length}
                </span>
              </div>

              {/* Cards */}
              <div style={{
                overflow: 'auto', padding: 'var(--space-3)',
                display: 'flex', flexDirection: 'column', gap: 'var(--space-2)',
                minHeight: 80,
              }}>
                {col.orders.length === 0 ? (
                  <div style={{ padding: 'var(--space-4)', textAlign: 'center' }}>
                    <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {t('kanban.empty_column')}
                    </p>
                  </div>
                ) : col.orders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    currency={currency}
                    isDragging={dragOrderId === order.id}
                    onDragStart={() => onCardDragStart(order.id)}
                    onDragEnd={() => setDragOrderId(null)}
                    nextStageName={columns[columns.indexOf(col) + 1]?.name}
                    onMoveToNext={(() => { const nextCol = columns[columns.indexOf(col) + 1]; return nextCol ? () => moveOrder.mutate({ orderId: order.id, stageId: nextCol.stageId }, { onError: () => toast.error(t('kanban.move_error')) }) : undefined; })()}
                    onClick={() => navigate(`/orders/${order.id}`)}
                    priorityColor={PRIORITY_DOT[order.priority] ?? 'var(--color-text-muted)'}
                    tc={tc}
                  />
                ))}
              </div>
            </div>
          );
        })}

        {/* Add Stage CTA */}
        <button
          onClick={() => navigate('/settings/stages')}
          style={{
            width: 180, flexShrink: 0,
            padding: 'var(--space-4)', height: 80,
            background: 'none', border: '2px dashed var(--color-border)',
            borderRadius: 'var(--radius-lg)', cursor: 'pointer',
            fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 'var(--space-2)',
          }}
        >
          <Plus size={14} />
          Add Stage
        </button>
      </div>

      {createOpen && (
        <OrderModal onClose={() => setCreateOpen(false)} />
      )}
    </div>
  );
}

function OrderCard({
  order, currency, isDragging, onDragStart, onDragEnd, onClick, priorityColor, tc,
  nextStageName, onMoveToNext,
}: {
  order: Order;
  currency: string;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onClick: () => void;
  priorityColor: string;
  tc: (key: string) => string;
  nextStageName?: string | undefined;
  onMoveToNext?: (() => void) | undefined;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--color-bg)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.5 : 1,
        transition: 'opacity 100ms, box-shadow 100ms',
        userSelect: 'none',
        boxShadow: hovered && !isDragging ? 'var(--shadow-sm)' : 'none',
      }}
    >
      {/* Order number + priority dot */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono, monospace)' }}>
          {order.orderNumber}
        </span>
        <span
          title={`Priority: ${order.priority}`}
          style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: priorityColor, flexShrink: 0 }}
        />
      </div>

      {/* Name */}
      <p style={{
        fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
        color: 'var(--color-text)', lineHeight: 'var(--leading-snug)',
        marginBottom: 'var(--space-2)',
      }}>
        {order.name}
      </p>

      {/* Footer: due date + total */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        {order.dueDate ? (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {order.dueDate}
          </span>
        ) : <span />}
        {order.total !== '0' && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)', fontWeight: 'var(--font-medium)' }}>
            {currency} {parseFloat(order.total).toLocaleString()}
          </span>
        )}
      </div>

      {/* Status badge (only if not draft) */}
      {order.status !== 'draft' && (
        <div style={{ marginTop: 'var(--space-2)' }}>
          <span style={{
            fontSize: 'var(--text-xs)',
            color: order.status === 'complete' ? 'var(--color-success)'
              : order.status === 'cancelled' ? 'var(--color-danger)'
              : 'var(--color-brand)',
            background: order.status === 'complete' ? 'var(--color-success-light)'
              : order.status === 'cancelled' ? 'var(--color-danger-light)'
              : 'var(--color-brand-pale)',
            padding: '1px 6px', borderRadius: 'var(--radius-full)',
          }}>
            {tc(`status.${order.status}`)}
          </span>
        </div>
      )}

      {/* Quick move button — appears on hover */}
      {hovered && nextStageName && onMoveToNext && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onMoveToNext(); }}
          style={{
            marginTop: 'var(--space-2)', width: '100%',
            padding: '3px var(--space-2)', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-brand-light)',
            background: 'var(--color-brand-pale)', color: 'var(--color-brand)',
            fontSize: 'var(--text-xs)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 4,
          }}
        >
          → Move to {nextStageName}
        </button>
      )}
    </div>
  );
}
